import React, { useState, useEffect, useCallback } from "react";
import { SlidersHorizontal, Search, Users2, X } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import api from "../lib/api";
import { LANGUAGES, languageLabel, countryLabel, COUNTRIES, POPULATIONS, APPROACHES, PROFESSIONS, PROFESSION_CATEGORIES, INTENTS, WORKS_WITH, ENGAGEMENT_TYPES, GENDERS, professionCategoryLabel, localize } from "../lib/meta";
import { PsychologistCard } from "../components/PsychologistCard";
import { EmptyState } from "../components/EmptyState";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ANY = "__any__";
const BASE = {
  q: "", language: ANY, country: ANY, city: "", area: ANY, population: ANY, approach: ANY,
  online: false, in_person: false, paid: false, volunteer: false, accepting: false, insurance: false, verified_only: false,
  profession: ANY, profession_category: ANY, profession_group: ANY, specialty: "", works_with: ANY, engagement: ANY, gender: ANY,
  min_experience: ANY, country_of_origin: "", cultural_background: "",
};

export default function Directory({ variant = "find" }) {
  const { t, lang } = useLang();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [areas, setAreas] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [adv, setAdv] = useState(false);
  const [intent, setIntent] = useState(null);
  const [f, setF] = useState({ ...BASE });
  const [taxonomy, setTaxonomy] = useState(null);
  const [openCat, setOpenCat] = useState(null);

  useEffect(() => { api.get("/areas").then((r) => setAreas(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/taxonomy").then((r) => setTaxonomy(r.data)).catch(() => {}); }, []);

  // Group professions under their taxonomy category, sourced from /api/taxonomy (falls back to local meta).
  const catGroups = (taxonomy?.categories || PROFESSION_CATEGORIES).map((c) => ({
    value: c.value, label: professionCategoryLabel(c.value, lang),
    professions: taxonomy
      ? taxonomy.professions.filter((p) => p.group === c.value).map((p) => ({ value: p.value, label: localize(taxonomy.professions, p.value, lang) }))
      : PROFESSIONS.filter((p) => p.group === c.value).map((p) => ({ value: p.value, label: localize(PROFESSIONS, p.value, lang) })),
  })).filter((c) => c.professions.length > 0);
  const selectedGroupOf = (val) => catGroups.find((c) => c.professions.some((p) => p.value === val))?.value || null;

  // Language options + labels sourced from /api/taxonomy (falls back to local meta).
  const langOptions = taxonomy?.languages?.length ? taxonomy.languages : LANGUAGES.map((v) => ({ value: v }));
  const langLabel = (v) => {
    const d = taxonomy?.languages;
    return d?.some((x) => x.value === v) ? localize(d, v, lang) : languageLabel(v, lang);
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (f.q) params.q = f.q;
    if (f.language !== ANY) params.language = f.language;
    if (f.country !== ANY) params.country = f.country;
    if (f.city) params.city = f.city;
    if (f.area !== ANY) params.area = f.area;
    if (f.population !== ANY) params.population = f.population;
    if (f.approach !== ANY) params.approach = f.approach;
    if (f.online) params.online = true;
    if (f.in_person) params.in_person = true;
    if (f.paid) params.paid = true;
    if (f.volunteer) params.volunteer = true;
    if (f.accepting) params.accepting = true;
    if (f.insurance) params.insurance = true;
    if (f.verified_only) params.verified_only = true;
    if (f.profession !== ANY) params.profession = f.profession;
    if (f.profession_category !== ANY) params.profession_category = f.profession_category;
    if (f.profession_group !== ANY) params.profession_group = f.profession_group;
    if (f.specialty) params.specialty = f.specialty;
    if (f.works_with !== ANY) params.works_with = f.works_with;
    if (f.engagement !== ANY) params.engagement = f.engagement;
    if (f.gender !== ANY) params.gender = f.gender;
    if (f.min_experience !== ANY) params.min_experience = f.min_experience;
    if (f.country_of_origin) params.country_of_origin = f.country_of_origin;
    if (f.cultural_background) params.cultural_background = f.cultural_background;
    try {
      const { data } = await api.get("/psychologists", { params });
      setList(data);
    } catch { setList([]); }
    finally { setLoading(false); }
  }, [f]);

  useEffect(() => {
    const timer = setTimeout(fetchList, 300);
    return () => clearTimeout(timer);
  }, [fetchList]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const reset = () => { setIntent(null); setF({ ...BASE }); };

  const applyIntent = (it) => {
    if (intent === it.value) { setIntent(null); setF((p) => ({ ...p, profession: ANY, profession_category: ANY, engagement: ANY })); return; }
    setIntent(it.value);
    setF((p) => ({
      ...p,
      profession: it.apply.profession || ANY,
      profession_category: it.apply.profession_category || ANY,
      engagement: it.apply.engagement || ANY,
    }));
  };
  const pickProfession = (val) => { setIntent(null); setF((p) => ({ ...p, profession: val, profession_category: ANY })); };

  const title = variant === "directory" ? t("directory.title") : t("find.title");
  const subtitle = variant === "directory" ? t("directory.subtitle") : t("find.subtitle");

  const filtersJsx = (
    <div className="space-y-5">
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("find.f_category")}</label>
        <Select value={f.profession_group} onValueChange={(v) => set("profession_group", v)}>
          <SelectTrigger className="mt-1.5" data-testid="filter-group-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ANY}>{t("common.all")}</SelectItem>{PROFESSION_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{professionCategoryLabel(c.value, lang)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("find.f_profession")}</label>
        <Select value={f.profession} onValueChange={(v) => set("profession", v)}>
          <SelectTrigger className="mt-1.5" data-testid="filter-profession-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ANY}>{t("common.all")}</SelectItem>{PROFESSIONS.map((p) => <SelectItem key={p.value} value={p.value}>{localize(PROFESSIONS, p.value, lang)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("find.f_language")}</label>
        <Select value={f.language} onValueChange={(v) => set("language", v)}>
          <SelectTrigger className="mt-1.5" data-testid="filter-language-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ANY}>{t("common.all")}</SelectItem>{langOptions.map((l) => <SelectItem key={l.value} value={l.value}>{langLabel(l.value)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("find.f_country")}</label>
        <Select value={f.country} onValueChange={(v) => set("country", v)}>
          <SelectTrigger className="mt-1.5" data-testid="filter-country-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ANY}>{t("common.all")}</SelectItem>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{countryLabel(c, lang)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <button type="button" onClick={() => setAdv((s) => !s)} data-testid="toggle-advanced-filters" className="flex w-full items-center justify-between border-t border-border pt-4 text-sm font-semibold text-slateink">
          {t("find.advanced")}
          <span className="text-terracotta">{adv ? "−" : "+"}</span>
        </button>
      </div>
      {adv && (<>
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("find.f_city")}</label>
        <Input className="mt-1.5" placeholder={t("find.city_placeholder")} value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="filter-city-input" />
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("find.f_area")}</label>
        <Select value={f.area} onValueChange={(v) => set("area", v)}>
          <SelectTrigger className="mt-1.5" data-testid="filter-area-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>{t("common.all")}</SelectItem>
            {areas.map((a) => { const n = localize([{ value: a.id, es: a.name_es, en: a.name_en }], a.id, lang); return <SelectItem key={a.id} value={n}>{n}</SelectItem>; })}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("find.f_population")}</label>
        <Select value={f.population} onValueChange={(v) => set("population", v)}>
          <SelectTrigger className="mt-1.5" data-testid="filter-population-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>{t("common.all")}</SelectItem>
            {POPULATIONS.map((p) => <SelectItem key={p.value} value={p.value}>{localize(POPULATIONS, p.value, lang)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("find.f_approach")}</label>
        <Select value={f.approach} onValueChange={(v) => set("approach", v)}>
          <SelectTrigger className="mt-1.5" data-testid="filter-approach-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>{t("common.all")}</SelectItem>
            {APPROACHES.map((a) => <SelectItem key={a.value} value={a.value}>{localize(APPROACHES, a.value, lang)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("find.f_works_with")}</label>
        <Select value={f.works_with} onValueChange={(v) => set("works_with", v)}>
          <SelectTrigger className="mt-1.5" data-testid="filter-works-with-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ANY}>{t("common.all")}</SelectItem>{WORKS_WITH.map((w) => <SelectItem key={w.value} value={w.value}>{localize(WORKS_WITH, w.value, lang)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("find.f_engagement")}</label>
        <Select value={f.engagement} onValueChange={(v) => set("engagement", v)}>
          <SelectTrigger className="mt-1.5" data-testid="filter-engagement-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ANY}>{t("common.all")}</SelectItem>{ENGAGEMENT_TYPES.map((e) => <SelectItem key={e.value} value={e.value}>{localize(ENGAGEMENT_TYPES, e.value, lang)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("find.f_experience")}</label>
        <Select value={f.min_experience} onValueChange={(v) => set("min_experience", v)}>
          <SelectTrigger className="mt-1.5" data-testid="filter-experience-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>{t("common.all")}</SelectItem>
            {["2", "5", "10"].map((y) => <SelectItem key={y} value={y}>{y}+ {t("find.years")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("find.f_gender")}</label>
        <Select value={f.gender} onValueChange={(v) => set("gender", v)}>
          <SelectTrigger className="mt-1.5" data-testid="filter-gender-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ANY}>{t("common.all")}</SelectItem>{GENDERS.map((g) => <SelectItem key={g.value} value={g.value}>{localize(GENDERS, g.value, lang)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("find.f_origin")}</label>
        <Input className="mt-1.5" value={f.country_of_origin} onChange={(e) => set("country_of_origin", e.target.value)} data-testid="filter-origin-input" />
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("find.f_culture")}</label>
        <Input className="mt-1.5" value={f.cultural_background} onChange={(e) => set("cultural_background", e.target.value)} data-testid="filter-culture-input" />
      </div>
      </>)}
      <div className="space-y-3 border-t border-border pt-4">
        {[["verified_only", t("find.f_verified_only")], ["online", t("find.f_online")], ["in_person", t("find.f_in_person")], ["paid", t("find.f_paid")], ["volunteer", t("find.f_volunteer")], ["accepting", t("find.f_accepting")], ["insurance", t("find.f_insurance")]].map(([k, label]) => (
          <label key={k} className="flex items-center gap-2.5 cursor-pointer">
            <Checkbox checked={f[k]} onCheckedChange={(v) => set(k, !!v)} data-testid={`filter-${k}-checkbox`} />
            <span className="text-sm text-slateink/80">{label}</span>
          </label>
        ))}
      </div>
      <Button variant="ghost" size="sm" onClick={reset} className="w-full" data-testid="filter-reset-btn"><X className="mr-1.5 h-4 w-4" />{t("common.clear")}</Button>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <h1 className="font-display text-3xl font-bold tracking-tight text-slateink sm:text-4xl">{title}</h1>
        <p className="mt-3 text-muted-foreground">{subtitle}</p>
      </div>

      {/* Intent-led discovery */}
      <div className="mt-8" data-testid="intent-discovery">
        {variant === "find" && <p className="text-xs font-semibold uppercase tracking-widest text-terracotta">{t("find.guided_label")}</p>}
        <p className="mt-1 text-sm font-semibold text-slateink">{t("find.intent_q")}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {INTENTS.map((it) => (
            <button key={it.value} onClick={() => applyIntent(it)} data-testid={`intent-${it.value}`}
              className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${intent === it.value ? "border-terracotta bg-terracotta text-white" : "border-border text-slateink/80 hover:border-terracotta/40"}`}>
              {localize(INTENTS, it.value, lang)}
            </button>
          ))}
        </div>
      </div>

      {/* Profession-first discovery — grouped by taxonomy category, collapsible */}
      <div className="mt-6" data-testid="profession-discovery">
        <p className="text-sm font-semibold text-slateink">{t("find.profession_q")}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => pickProfession(ANY)} data-testid="profession-pick-all"
            className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${f.profession === ANY ? "border-slateink bg-slateink text-white" : "border-border text-slateink/80 hover:border-slateink/40"}`}>
            {t("common.all")}
          </button>
        </div>
        <div className="mt-3 space-y-2">
          {catGroups.map((c) => {
            const isOpen = openCat === c.value || selectedGroupOf(f.profession) === c.value;
            const selectedInCat = c.professions.find((p) => p.value === f.profession);
            return (
              <div key={c.value} className="rounded-xl border border-border" data-testid={`prof-cat-group-${c.value}`}>
                <button type="button" onClick={() => setOpenCat(isOpen ? "__none__" : c.value)} data-testid={`prof-cat-toggle-${c.value}`}
                  className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left">
                  <span className="text-sm font-semibold text-slateink">{c.label}</span>
                  <span className="flex items-center gap-2">
                    {selectedInCat && <span className="rounded-full bg-terracotta px-2 py-0.5 text-[11px] font-medium text-white" data-testid={`prof-cat-selected-${c.value}`}>{selectedInCat.label}</span>}
                    <span className="text-muted-foreground">{isOpen ? "−" : "+"}</span>
                  </span>
                </button>
                {isOpen && (
                  <div className="flex flex-wrap gap-2 border-t border-border px-4 py-3">
                    {c.professions.map((p) => (
                      <button key={p.value} onClick={() => pickProfession(p.value)} data-testid={`profession-pick-${p.value}`}
                        className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${f.profession === p.value ? "border-slateink bg-slateink text-white" : "border-border text-slateink/80 hover:border-slateink/40"}`}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-8">
        {variant === "find" && (
          <div className="mb-3" data-testid="direct-search-header">
            <p className="text-xs font-semibold uppercase tracking-widest text-terracotta">{t("find.direct_label")}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t("find.direct_hint")}</p>
          </div>
        )}
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-10" placeholder={t("common.search")} value={f.q} onChange={(e) => set("q", e.target.value)} data-testid="search-psychologists-input" />
          </div>
          <Button variant="outline" onClick={() => setShowFilters((s) => !s)} className="lg:hidden" data-testid="toggle-filters-btn">
            <SlidersHorizontal className="mr-2 h-4 w-4" /> {t("common.filters")}
          </Button>
        </div>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[260px_1fr]">
        <aside className={`${showFilters ? "block" : "hidden"} lg:block`} data-testid="filters-panel">
          <div className="rounded-2xl border border-border bg-white p-6 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
            {filtersJsx}
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="grid gap-5 sm:grid-cols-2">
              {[0, 1, 2, 3].map((i) => <div key={i} className="h-52 animate-pulse rounded-2xl bg-white/60" />)}
            </div>
          ) : list.length === 0 ? (
            <EmptyState icon={Users2} title={t("find.empty_title")} description={t("find.empty_desc")} testId="directory-empty" />
          ) : (
            <>
              <p className="mb-5 text-sm text-muted-foreground" data-testid="results-count">
                {list.length} {list.length === 1 ? t("find.result") : t("find.results")}
              </p>
              <div className="grid gap-5 sm:grid-cols-2">
                {list.map((p) => <PsychologistCard key={p.id} p={p} />)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
